import { createColumnHelper, Row } from '@tanstack/react-table';
import { HeadCell, SortableHeadCell, TextCell } from 'components/tables/cells';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import {
	NumberColumnProps,
	TextColumnProps,
} from 'components/tables/columnTypes';
import ApplicationCell from './ApplicationCell';
import PlanCell from './PlanCell';
import {
	getSubscriptionStatusColor,
	Subscription,
} from 'domain/subscription/interfaces/Subscription';
import { RowExpandedIndicatorCell } from 'components/tables/cells/RowExpandedIndicatorCell/RowExpandedIndicatorCell';
import { SubscriptionOwnerCell } from './SubscriptionOwnerCell';

const columnHelper = createColumnHelper<Subscription>();

export const subscriptionTableColumns = {
	application: (overrideProps?: TextColumnProps<Subscription>) =>
		columnHelper.accessor((row) => 'applicationIds', {
			id: 'application',
			enableSorting: true,
			header: ({ header }) => (
				<HeadCell
					text="Product(s)"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<ApplicationCell
					permissionSets={info.row.original.applicationPermissions}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	plan: (
		applicationId?: string,
		overrideProps?: TextColumnProps<Subscription>,
	) =>
		columnHelper.accessor((row) => 'plans', {
			id: 'plan',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Plan(s)"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<PlanCell
					permissionSets={info.row.original.applicationPermissions}
					applicationId={applicationId}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	type: (overrideProps?: TextColumnProps<Subscription>) =>
		columnHelper.accessor((row) => row.type, {
			id: 'type',
			enableSorting: true,
			filterFn: (row, id, filterValue) => {
				if (Array.isArray(filterValue)) {
					return filterValue.includes(row.getValue(id));
				}

				return row.getValue(id) === filterValue;
			},
			header: ({ header }) => (
				<SortableHeadCell
					text="Type"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	startDate: (overrideProps?: TextColumnProps<Subscription>) =>
		columnHelper.accessor((row) => row.startDate, {
			id: 'startDate',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Start Date"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'N/A',
						'DD MMM YYYY',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	endDate: (overrideProps?: TextColumnProps<Subscription>) =>
		columnHelper.accessor((row) => row.endDate, {
			id: 'endDate',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="End Date"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={toDefaultDateFormatOrNotAvailable(
						info.getValue(),
						'N/A',
						'DD MMM YYYY',
					)}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	status: (overrideProps?: TextColumnProps<Subscription>) =>
		columnHelper.accessor((row) => row.status, {
			id: 'status',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Status"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					color={getSubscriptionStatusColor(info.getValue())}
					{...overrideProps?.cellProps}
				/>
			),
			filterFn: (row, id, filterValue) => {
				if (Array.isArray(filterValue)) {
					return filterValue.includes(row.getValue(id));
				}

				return row.getValue(id) === filterValue;
			},
			...overrideProps?.columnDefProps,
		}),
	seats: (overrideProps?: NumberColumnProps<Subscription>) =>
		columnHelper.accessor((row) => row.seats, {
			id: 'seats',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Seats"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={
						Number.isInteger(info.row.original.occupiedSeats)
							? `${
									info.row.original.occupiedSeats
							  }/${info.getValue()}`
							: `${info.getValue()}`
					}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	company: (overrideProps?: TextColumnProps<Subscription>) =>
		columnHelper.accessor((row) => row.companyName, {
			id: 'companyName',
			enableSorting: true,
			filterFn: (
				row: Row<Subscription>,
				columnId: string,
				filterValue: any,
			): boolean => row.getValue(columnId) === filterValue,
			header: ({ header }) => (
				<SortableHeadCell
					text="Company"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	owner: (
		expanded?: boolean,
		overrideProps?: TextColumnProps<Subscription>,
	) =>
		columnHelper.accessor((row) => row.companyId?.toString(), {
			id: 'owner',
			enableSorting: true,
			meta: expanded ? undefined : { width: '20px' },
			header: ({ header }) => (
				<SortableHeadCell
					text="Owner"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<SubscriptionOwnerCell
					subscription={info.row.original}
					expanded={expanded}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	description: (overrideProps?: TextColumnProps<Subscription>) =>
		columnHelper.accessor((row) => row.description, {
			id: 'description',
			enableSorting: true,
			header: ({ header }) => (
				<SortableHeadCell
					text="Description"
					header={header}
					{...overrideProps?.headCellProps}
				/>
			),
			cell: (info) => (
				<TextCell
					text={info.getValue()}
					withTooltip
					tooltipText={info.getValue()}
					{...overrideProps?.cellProps}
				/>
			),
			...overrideProps?.columnDefProps,
		}),
	expandedIndicator: () =>
		columnHelper.display({
			id: 'expandedIndicator',
			enableSorting: false,
			meta: { width: '20px' },
			header: () => <HeadCell text="" />,
			cell: (info) =>
				info.row.getIsExpanded() ? (
					<RowExpandedIndicatorCell isExpanded={false} />
				) : (
					<RowExpandedIndicatorCell isExpanded />
				),
		}),
};
